// A small hand-drawn SVG line chart (no chart library). Several series share
// one x axis, oldest on the left and newest on the right. A missing value
// (null) is a GAP: the line breaks and no dot is drawn. It is never zero.
import type { JSX } from 'preact';

export interface ChartSeries {
  key: string;
  label: string;
  /** One entry per x label; null = no data for that x. */
  values: (number | null)[];
}
export interface ChartSelection { seriesKey: string; index: number }

const W = 520;
const H = 180;
const PAD = { l: 38, r: 10, t: 10, b: 24 };
export const SERIES_COLORS = [
  'var(--color-brand-purple)', 'var(--color-ok)', 'var(--color-warn)', 'var(--color-fault)',
];

/** Split a series into runs of consecutive real values, so gaps break the line. */
export function lineRuns(values: (number | null)[]): number[][] {
  const runs: number[][] = [];
  let cur: number[] = [];
  values.forEach((v, i) => {
    if (v === null || v === undefined || Number.isNaN(v)) {
      if (cur.length) runs.push(cur);
      cur = [];
    } else cur.push(i);
  });
  if (cur.length) runs.push(cur);
  return runs;
}

export function LineChart(
  { labels, series, yMax, selected, onSelect, testId = 'line-chart' }:
  {
    labels: string[];
    series: ChartSeries[];
    yMax?: number;
    selected?: ChartSelection | null;
    onSelect?: (s: ChartSelection) => void;
    testId?: string;
  },
): JSX.Element {
  const all = series.flatMap((s) => s.values).filter((v): v is number => typeof v === 'number');
  const top = yMax ?? Math.max(1, ...all);
  const n = labels.length;
  const x = (i: number): number => (n <= 1 ? (PAD.l + W - PAD.r) / 2 : PAD.l + (i * (W - PAD.l - PAD.r)) / (n - 1));
  const y = (v: number): number => PAD.t + (1 - v / top) * (H - PAD.t - PAD.b);
  const color = (si: number): string => SERIES_COLORS[si % SERIES_COLORS.length];
  const ticks = [0, top / 2, top];
  return (
    <div data-testid={testId}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={series.map((s) => s.label).join(', ')}
        style={{ width: '100%', height: 'auto', maxWidth: `${W}px` }}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} stroke="var(--color-border)" stroke-width="1" />
            <text x={PAD.l - 4} y={y(t) + 3} text-anchor="end" font-size="9" fill="var(--color-text-muted)">
              {Math.round(t * 10) / 10}
            </text>
          </g>
        ))}
        {n > 0 && (
          <>
            <text x={PAD.l} y={H - 8} font-size="9" fill="var(--color-text-muted)">{labels[0]}</text>
            <text x={W - PAD.r} y={H - 8} text-anchor="end" font-size="9" fill="var(--color-text-muted)">{labels[n - 1]}</text>
          </>
        )}
        {series.map((s, si) => (
          <g key={s.key} data-series={s.key}>
            {lineRuns(s.values).map((run) => (run.length > 1 ? (
              <polyline key={run[0]} data-testid="line-run" fill="none" stroke={color(si)} stroke-width="2"
                points={run.map((i) => `${x(i)},${y(s.values[i] as number)}`).join(' ')} />
            ) : null))}
            {s.values.map((v, i) => (v === null || v === undefined ? null : (
              <circle key={i} data-testid="chart-point" data-series={s.key} data-index={i}
                cx={x(i)} cy={y(v)}
                r={selected && selected.seriesKey === s.key && selected.index === i ? 5 : 3}
                fill={color(si)} style={{ cursor: 'pointer' }}
                onClick={() => onSelect?.({ seriesKey: s.key, index: i })}
                onMouseEnter={() => onSelect?.({ seriesKey: s.key, index: i })}>
                <title>{`${s.label}: ${v} (${labels[i]})`}</title>
              </circle>
            )))}
          </g>
        ))}
      </svg>
      <div data-testid="chart-legend" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '12px' }}>
        {series.map((s, si) => (
          <span key={s.key}>
            <span aria-hidden="true" style={{ display: 'inline-block', width: '10px', height: '10px', background: color(si), marginRight: '4px' }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}
